<template>
	<ui-root v-if="loaded" :data="pref" :part="form" ref="frm" :errs="errors" :actions="dialogControls(env)"></ui-root>
</template>

<script setup lang="ts">
import {onMounted, onUnmounted, ref, watch, reactive, computed} from "vue";
import {connectEventBus, injectEnv} from "@fd/Environment";
import type {Pref} from "@t/setup/Pref";
import type {WithMeta} from "@t/fd/WithMeta";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition";
import type {ErrorMap} from "@fd/PartInjectKeys";
import {NetworkResponse} from "@fd/Types";
import {useRouter,useRoute} from "vue-router";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import UiRoot from "@fd/containers/UiRoot.vue";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {useVirtualRoute} from "@/router/virtual/VirtualRouter.ts";


const env = injectEnv();
const navStore = useNavigationStore();
const router = useRouter();
const route = useVirtualRoute();

const loaded = ref<boolean>(false);

const errors = reactive<ErrorMap>([]);
const pref = ref<Pref|null>(null);
const form = ref<InterfaceRoot<FormDefinition>|null>(null);
const bus = connectEventBus("PrefPage");
const frm = ref(null);

const prefID = computed<string>(() => {
	if (pref.value?.pkey && pref.value?.crmUser) {
		return pref.value?.pkey + "@" + pref.value?.crmUser;
	}
	return pref.value?.pkey || "";
});

function receivedData(data:Pref & WithMeta):void {
	if (data.META) {
		form.value = data.META.form;
		delete data.META;
	}
	pref.value = data;
	loaded.value = true;
	if (pref.value && pref.value.pkey) {
		navStore.replace(pref.value.pkey);
	}
}

const newEntry = ref<boolean>(true);

function loadData() {
	let endpoint = null;
	const id = route.query.id
	if (id) {
		endpoint = "api/prefs/"+encodeURIComponent(id)+"?meta=true";
		newEntry.value = false;
	} else {
		endpoint = "api/prefs/init?meta=true";
		newEntry.value = true;
	}
	return env.net.fetch(endpoint)
			.then((result) => result.json())
			.then(receivedData);
}

function saveData():Promise<NetworkResponse> {
    return env.net.fetch("api/prefs/"+encodeURIComponent(prefID.value), "PUT", pref.value);
}

function createData():Promise<NetworkResponse> {
    return env.net.fetch("api/prefs", "POST", pref.value).then((nr) => {
		if (nr.ok) newEntry.value = false;
		return nr;
	});
}

function sendData():Promise<NetworkResponse> {
	return newEntry.value ? createData() : saveData();
}

bus.onCommand(["save", "saveAndBack"], async (cmd) => {
	errors.length = 0;
	const response = await sendData();
	if (response.ok) {
		if (cmd === "save") {
			const data = await response.json();
			receivedData(data);
			if (frm.value) {
				frm.value.setDirty(false);
			}
		} else {
			// Go back
			router.back();
		}
		return "ok";
	} else {
		const data = await response.json();
		if (Array.isArray(data?.problems)) {
			for (let problem of data.problems) {
				errors.push(problem);
			}
		}
		console.error(response);
		return "fail";
	}
});

watch(() => route.query.id, loadData, { immediate: true });
navStore.defineMenu((mb) => mb.standardMenu());

onMounted(() => {
	navStore.forward(env.msg("menu.createPref"));
});
</script>

<style>

</style>

